import {
    IonBadge,
    IonIcon,
    IonItem,
    IonItemOption,
    IonItemOptions,
    IonItemSliding,
    IonList,
    useIonAlert,
    useIonRouter,
    useIonViewWillEnter,
} from '@ionic/react'
import { trashOutline } from 'ionicons/icons'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextSelector } from 'use-context-selector'

import { AppContext } from '../../components/AppState'
import ContactPartner from '../../components/Contact/ContactPartner'
import { dayLabel, messageTime } from '../../components/Contact/format'
import Content from '../../components/Content'
import Header from '../../components/Header'
import InboxTabs from '../../components/Inbox/InboxTabs'
import Page from '../../components/Page'
import type { ContactConversationListItemFragment } from '../../lib/apollo/types'
import {
    ContactRequestStatus,
    useContactConversationsQuery,
    useDeleteContactConversationMutation,
} from '../../lib/apollo/types'
import useToast from '../../providers/Toast/hooks'

import './style.scss'

/**
 * Der Posteingang. Er zeigt bewusst auch die offenen Anfragen mit — eine
 * Unterhaltung soll an einer Stelle auffindbar sein, unabhaengig davon, in
 * welchem Zustand sie gerade ist.
 */
const ContactConversations: React.FC = () => {
    const { t } = useTranslation()
    const router = useIonRouter()
    const [confirm] = useIonAlert()
    const [present] = useToast()

    // Ionic laesst eine aufgezogene Zeile sonst offen stehen, auch nachdem die
    // Unterhaltung darunter verschwunden ist.
    const slidingRefs = useRef<Record<string, HTMLIonItemSlidingElement | null>>(
        {},
    )

    const { pendingContactRequests } = useContextSelector(
        AppContext,
        (state) => state.state,
    )

    const { data, loading, refetch } = useContactConversationsQuery()

    const [remove] = useDeleteContactConversationMutation({
        // Auch die schlanke Badge-Abfrage: sonst zaehlt die Tab-Leiste
        // ungelesene Nachrichten einer Unterhaltung mit, die es nicht mehr gibt.
        refetchQueries: ['contactConversations', 'contactUnread'],
        onError: () => {
            present(
                t(
                    'contact.conversation.deleteFailed',
                    'Die Unterhaltung konnte nicht gelöscht werden.',
                ),
                'danger',
            )
        },
    })

    // Nach dem Lesen eines Chats stimmt der Zaehler hier nicht mehr.
    useIonViewWillEnter(() => {
        refetch()
    })

    const onDelete = useCallback(
        (conversation: ContactConversationListItemFragment) => {
            slidingRefs.current[conversation.id]?.close()

            confirm({
                header: t('contact.conversation.delete', 'Unterhaltung löschen'),
                message: t(
                    'contact.conversation.deleteConfirm',
                    'Die Unterhaltung verschwindet aus deinem Posteingang. Die Gegenseite behält ihren Verlauf.',
                ),
                buttons: [
                    {
                        text: t('buttons.cancel'),
                        role: 'cancel',
                    },
                    {
                        text: t('buttons.delete'),
                        handler: () => {
                            remove({ variables: { id: conversation.id } })
                        },
                    },
                ],
            })
        },
        [confirm, remove, t],
    )

    const conversations = data?.contactConversations ?? []

    const nameOf = (conversation: ContactConversationListItemFragment) =>
        conversation.counterpart
            ? `${conversation.counterpart.firstName} ${conversation.counterpart.lastName}`
            : conversation.request?.target?.name

    const imageOf = (conversation: ContactConversationListItemFragment) =>
        conversation.counterpart?.image ?? conversation.request?.target?.image

    const sublineOf = (conversation: ContactConversationListItemFragment) => {
        const message = conversation.lastMessage
        if (!message) return undefined

        const when = `${dayLabel(message.createdAt, t)}, ${messageTime(message.createdAt)}`

        if (conversation.status === ContactRequestStatus.Pending) {
            return `${t('contact.conversation.pendingShort', 'Anfrage offen')} · ${when}`
        }

        return when
    }

    return (
        <Page trackingTitle='Contact Conversations'>
            <Header
                buttonLeft='menu'
                title={t('title.inbox', 'Postfach')}
            />
            <Content>
                <InboxTabs
                    active='chats'
                    requestCount={pendingContactRequests?.length}
                />
                {!loading && conversations.length === 0 && (
                    <div className='contact-conversations'>
                        <p className='contact-conversations__empty ion-padding'>
                            {t(
                                'contact.conversations.empty',
                                'Noch keine Unterhaltungen. Sobald du jemanden kontaktierst oder eine Anfrage annimmst, findest du sie hier.',
                            )}
                        </p>
                    </div>
                )}

                {/*
                  * `IonList` ist fuer die Wischgeste kein Beiwerk: Ionic
                  * schliesst eine offene Zeile nur dann automatisch, wenn es
                  * eine Liste findet, in der die Zeilen zusammen haengen.
                  */}
                <IonList className='contact-conversations'>
                    {conversations.map((conversation) => (
                        <IonItemSliding
                            key={conversation.id}
                            ref={(element) => {
                                slidingRefs.current[conversation.id] = element
                            }}
                        >
                            <IonItem
                                lines='full'
                                button
                                detail={false}
                                className='contact-conversations__item'
                                onClick={() =>
                                    router.push(
                                        `/inbox/conversations/${conversation.id}`,
                                    )
                                }
                            >
                                <div className='contact-conversations__row'>
                                    <ContactPartner
                                        name={nameOf(conversation)}
                                        image={imageOf(conversation)}
                                        subline={sublineOf(conversation)}
                                    />
                                    <p className='contact-conversations__preview'>
                                        {conversation.lastMessage?.body}
                                    </p>
                                </div>
                                {conversation.unreadCount > 0 && (
                                    <IonBadge
                                        slot='end'
                                        color='secondary'
                                    >
                                        {conversation.unreadCount}
                                    </IonBadge>
                                )}
                            </IonItem>

                            {/*
                              * `onIonSwipe` plus `expandable` gibt der Geste
                              * ihren zweiten Teil: Wer weit genug zieht, muss
                              * den Knopf nicht mehr treffen — die Zeile laeuft
                              * durch und loest selbst aus, wie in Mail.
                              */}
                            <IonItemOptions
                                side='end'
                                onIonSwipe={() => onDelete(conversation)}
                            >
                                <IonItemOption
                                    expandable
                                    color='danger'
                                    onClick={() => onDelete(conversation)}
                                >
                                    <IonIcon
                                        slot='start'
                                        icon={trashOutline}
                                    />
                                    {t('buttons.delete')}
                                </IonItemOption>
                            </IonItemOptions>
                        </IonItemSliding>
                    ))}
                </IonList>
            </Content>
        </Page>
    )
}

export default ContactConversations
